import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText, Lightbulb, ListChecks } from 'lucide-react';
import GuideAccountCTA from '../components/GuideAccountCTA';
import './ApplicationTimelinePage.css';

const topTips = [
  'Build around fit, affordability, deadlines, and actual interest, not prestige panic.',
  'Start broad, then narrow once you understand cost, programs, location, and workload.',
  'No school is truly safe unless it is affordable, realistic, and somewhere you would be willing to attend.',
  'More colleges means more essays, fees, portals, scholarship forms, and things to track.',
  'Use dashboards for research and Progress only for the final schools you are actively applying to.',
];

const sections = [
  {
    title: 'Start broad, then narrow',
    items: [
      'Begin with possibilities: schools that match your academic interests, location preferences, cost range, and campus style.',
      'Then cut schools that do not fit your actual constraints or that you would not be excited to attend.',
      'Keep notes on why each school is on the list so the decision does not become random later.',
    ],
  },
  {
    title: 'Use reach, target, and likely carefully',
    items: [
      'Categories should consider acceptance rate, GPA/test profile, major selectivity, cost, and special program requirements.',
      'A likely school still needs to be affordable and a place you would attend.',
      'For very selective schools, strong grades and scores do not make admission predictable.',
    ],
  },
  {
    title: 'Check fit beyond ranking',
    items: [
      'Look at major strength, course options, location, size, campus culture, support, distance from home, weather, internships, housing, and student life.',
      'Ask whether you can picture normal weekdays there, not just the acceptance letter.',
      'Use visits, virtual sessions, student videos, and official department pages to make the school feel real.',
    ],
  },
  {
    title: 'Check affordability early',
    items: [
      'Use net price calculators and talk with family about budget before the list is final.',
      'Track FAFSA, CSS Profile, scholarship, honors college, and priority aid deadlines.',
      'A school with a great admit offer can still be a poor fit if the cost is not workable.',
    ],
  },
  {
    title: 'Watch application workload',
    items: [
      'Every added school can mean more supplements, fees, portals, recommendation rules, and scholarship forms.',
      'Be honest about how much writing and tracking you can finish well.',
      'A shorter thoughtful list is usually better than a huge list you cannot manage.',
    ],
  },
  {
    title: 'Build the final list',
    items: [
      'Dashboards are for researching, comparing, and narrowing schools.',
      'Progress Final College List is for schools you are actively applying to.',
      'Once a school moves to Progress, set deadlines, tasks, submitted status, and portal follow-ups.',
    ],
  },
  {
    title: 'Confirm requirements',
    items: [
      'Deadlines, test policy, recommendations, essays, portfolios, CSS Profile, FAFSA priority dates, and scholarships vary by school.',
      'Once you add colleges to Common App, check each school’s requirements directly.',
      'Also check official admissions and financial aid pages because requirements can change.',
    ],
  },
];

const maiCollegesTips = [
  'Use the Searching Process dashboard to compare possible schools by fit, cost, location, scores, acceptance rate, and notes.',
  'Use the College Tours dashboard to record campus feel, visit notes, questions, pros, and cons.',
  'Move only active application schools into Progress Final College List when you are done narrowing.',
  'Use Calendar after you set deadlines and dated tasks in Progress.',
];

const sources = [
  { label: 'Common App first-year guide', url: 'https://www.commonapp.org/apply/first-year-students/' },
  { label: 'BigFuture college search', url: 'https://bigfuture.collegeboard.org/college-search' },
  { label: 'FAFSA', url: 'https://studentaid.gov/h/apply-for-aid/fafsa' },
  { label: 'CSS Profile', url: 'https://cssprofile.collegeboard.org/' },
];

function CollegeListGuidePage() {
  return (
    <main className="timeline-page">
      <div className="timeline-page__inner">
        <header className="timeline-page__hero">
          <span className="timeline-page__eyebrow">College List Guide</span>
          <h1>Build a list that actually works</h1>
          <p>
            A strong college list is not just a pile of famous names. It balances fit, affordability,
            admission chances, application workload, and whether you would genuinely go there.
          </p>
        </header>

        <section className="timeline-page__intro" aria-label="College list reminder">
          <div className="timeline-page__intro-icon">
            <FileText size={19} strokeWidth={1.8} />
          </div>
          <div>
            <h2>Research first, final list second</h2>
            <p>
              Use dashboards for the messy research stage. Once a school becomes a real application,
              move it into Progress so your deadlines, tasks, calendar, and reminders stay focused.
            </p>
          </div>
        </section>

        <section className="timeline-page__note timeline-page__note--action" aria-label="Use MaiColleges for college list work">
          <span className="timeline-page__action-kicker">Use MaiColleges</span>
          <h2>Research in dashboards, finalize in Progress</h2>
          <p>
            Use the Searching Process dashboard to compare schools and the College Tours dashboard to capture
            visit notes, questions, pros, and cons. When the list becomes real, put those colleges in
            Progress under Final College List so Home, Calendar, reminders, and Individual Progress stay focused.
          </p>
          <div className="timeline-page__action-row">
            <Link to="/dashboard" className="timeline-page__internal-link">Open Dashboards</Link>
            <Link to="/progress" className="timeline-page__internal-link timeline-page__internal-link--secondary">Open Final College List</Link>
          </div>
        </section>

        <section className="timeline-page__tips" aria-labelledby="college-list-tips-title">
          <div className="timeline-page__section-heading">
            <Lightbulb size={18} strokeWidth={1.8} />
            <h2 id="college-list-tips-title">Top tips</h2>
          </div>
          <div className="timeline-page__tip-grid">
            {topTips.map((tip) => (
              <article className="timeline-tip" key={tip}>
                <ListChecks size={16} strokeWidth={1.9} />
                <p>{tip}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="timeline-page__timeline" aria-labelledby="college-list-plan-title">
          <div className="timeline-page__section-heading">
            <ListChecks size={18} strokeWidth={1.8} />
            <h2 id="college-list-plan-title">List-building plan</h2>
          </div>
          <div className="timeline-list">
            {sections.map((section) => (
              <article className="timeline-block" key={section.title}>
                <div className="timeline-block__marker" aria-hidden="true" />
                <div className="timeline-block__content">
                  <h3>{section.title}</h3>
                  <ul>
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="timeline-page__note" aria-label="Use MaiColleges for college lists">
          <h2>Use MaiColleges for this</h2>
          <ul className="timeline-page__note-list">
            {maiCollegesTips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </section>

        <section className="timeline-page__sources" aria-labelledby="college-list-sources-title">
          <h2 id="college-list-sources-title">Helpful official sources</h2>
          <div className="timeline-source-grid">
            {sources.map((source) => (
              <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>
                <span>{source.label}</span>
                <ExternalLink size={14} strokeWidth={1.9} />
              </a>
            ))}
          </div>
        </section>

        <GuideAccountCTA title="Build a dashboard for your summer college list" />
      </div>
    </main>
  );
}

export default CollegeListGuidePage;
